/**
 * LACOMUS - small "Add" buttons (pair offer, cart upsell).
 *
 * Markup: <button data-lacomus-add='[{"id":123,"quantity":1}]' data-open-cart="true">
 *
 * Uses the theme's own cart pipeline: dispatches the standard
 * CartLinesUpdateEvent with the re-rendered cart sections, so the cart
 * drawer, cart page and cart bubble refresh exactly as they do for the
 * main Add to cart button. Shows a spinner through aria-busy.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const routes = window.Theme?.routes ?? {};
// The theme already exposes the add route with ".js" (see snippets/scripts.liquid).
const addUrl = routes.cart_add_url || '/cart/add.js';
const cartUrl = `${routes.cart_url || '/cart'}.js`;

/** @param {HTMLElement} button @param {string} message */
function showError(button, message) {
  const holder = button.closest('[data-lacomus-add-scope]') || button.parentElement;
  const error = holder?.querySelector('[data-lacomus-add-error]');
  if (error) error.textContent = message;
}

async function fetchCart() {
  const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
  return response.json();
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-lacomus-add]'));
  if (!button || button.getAttribute('aria-busy') === 'true') return;

  event.preventDefault();

  /** @type {{id: number, quantity: number}[]} */
  let items = [];
  try {
    items = JSON.parse(button.dataset.lacomusAdd || '[]');
  } catch {
    return;
  }
  if (!items.length) return;

  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);

  button.setAttribute('aria-busy', 'true');
  showError(button, '');

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: button.closest('cart-items-component') ? 'cart' : 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const data = await response.json();
    const cart = await fetchCart();
    const didError = Boolean(data.status);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'lacomus-add-lines',
        itemCount,
        sections: didError ? undefined : data.sections,
        didError,
      },
    });

    if (didError) {
      showError(button, data.description || data.message || 'Could not add to cart');
      return;
    }

    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), 1400);

    if (button.dataset.openCart === 'true') {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) drawer.open();
      else window.location.href = routes.cart_url || '/cart';
    }
  } catch (error) {
    deferred.reject(error);
    showError(button, 'Could not add to cart');
  } finally {
    button.removeAttribute('aria-busy');
  }
});
